import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Package, RefreshCw, Plus, Search, Settings, Truck, Trash2, Archive, Phone, MessageCircle, AlertTriangle, Clock } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import ContainersConfigPanel from '../components/ContainersConfigPanel';
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CT = `${API}/admin/logistics/containers`;
const authHeaders = () => {
  const t = localStorage.getItem('token');
  return t ? { Authorization: `Bearer ${t}` } : {};
};

const fmt = (n) => (n ?? 0).toLocaleString('ru-RU');
const formatTime = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleString('ru-RU', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
};


// =====================================================================
// Searchable Client Picker
// =====================================================================
function ClientPicker({ value, onChange, placeholder = "Найти клиента..." }) {
  const [q, setQ] = useState('');
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const containerRef = useRef(null);
  const debounceRef = useRef(null);

  // Close on outside click
  useEffect(() => {
    const onClick = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  // Load default (top movers) when first opened
  const doSearch = useCallback(async (query) => {
    setLoading(true);
    try {
      const url = query.trim().length >= 2
        ? `${CT}/clients-search?q=${encodeURIComponent(query.trim())}`
        : `${CT}/clients-search`;
      const r = await axios.get(url, { headers: authHeaders() });
      setResults(r.data || []);
    } catch {
      setResults([]);
    } finally {
      setLoading(false);
    }
  }, []);

  // Debounce search input
  useEffect(() => {
    if (!open) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => doSearch(q), 200);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
  }, [q, open, doSearch]);

  // Reset selection when value cleared from parent
  useEffect(() => {
    if (!value) setSelected(null);
  }, [value]);

  const pick = (c) => {
    setSelected(c);
    setQ('');
    setOpen(false);
    onChange(c.user_id, c);
  };

  const clear = () => {
    setSelected(null);
    setQ('');
    onChange('', null);
  };

  return (
    <div ref={containerRef} className="relative">
      {selected ? (
        <div className="flex items-center justify-between border rounded-md px-3 py-2 bg-emerald-50 border-emerald-200" data-testid="client-picker-selected">
          <div className="text-sm">
            <div className="font-medium">
              {selected.name}
              {selected.network_name && <span className="ml-1 text-blue-700 font-normal">• {selected.network_name}</span>}
            </div>
            <div className="text-xs text-gray-500">
              {selected.contact_person && <span>{selected.contact_person} • </span>}
              {selected.phone && <span>{selected.phone}</span>}
              {selected.has_movements && <span className="ml-1 text-amber-600">• текущий остаток: {selected.balance > 0 ? '+' : ''}{selected.balance}</span>}
            </div>
            {selected.last_address && (
              <div className="text-xs text-emerald-700 truncate mt-0.5">📍 {selected.last_address}</div>
            )}
          </div>
          <Button variant="ghost" size="sm" onClick={clear} data-testid="client-picker-clear">×</Button>
        </div>
      ) : (
        <>
          <div className="relative">
            <Search className="absolute left-2 top-2.5 w-4 h-4 text-gray-400 pointer-events-none" />
            <Input
              value={q}
              onChange={(e) => { setQ(e.target.value); setOpen(true); }}
              onFocus={() => { setOpen(true); if (results.length === 0) doSearch(''); }}
              placeholder={placeholder}
              className="pl-8"
              data-testid="client-picker-input"
            />
          </div>
          {open && (
            <div className="absolute z-50 left-0 right-0 mt-1 bg-white border rounded-md shadow-lg max-h-64 overflow-y-auto" data-testid="client-picker-dropdown">
              {loading && <div className="p-3 text-sm text-gray-400">Поиск…</div>}
              {!loading && results.length === 0 && (
                <div className="p-3 text-sm text-gray-400">
                  {q.trim().length < 2 && q.trim().length > 0
                    ? 'Введите минимум 2 символа'
                    : q.trim()
                      ? 'Ничего не найдено'
                      : 'Нет клиентов с движениями. Начните печатать имя.'}
                </div>
              )}
              {!loading && results.map((c) => (
                <button
                  key={c.user_id + (c.is_pool_owner ? '__pool' : '') + (c.network_id ? `__${c.network_id}` : '')}
                  type="button"
                  className={`w-full text-left px-3 py-2 hover:bg-gray-50 border-b border-gray-50 last:border-b-0 ${c.is_pool_owner ? 'bg-indigo-50/40' : ''}`}
                  onClick={() => pick(c)}
                  data-testid={`client-picker-option-${c.user_id}${c.is_pool_owner ? '-pool' : ''}${c.network_id ? `-${c.network_id.slice(0,8)}` : ''}`}
                >
                  <div className="flex items-baseline justify-between gap-2">
                    <div className={`font-medium text-sm truncate ${c.is_pool_owner ? 'text-indigo-900' : ''}`}>
                      {c.name}
                      {c.network_name && <span className="ml-1 text-blue-700 font-normal">• {c.network_name}</span>}
                    </div>
                    {c.has_movements ? (
                      <span className={`text-xs font-semibold whitespace-nowrap ${c.balance > 0 ? 'text-amber-600' : c.balance < 0 ? 'text-red-600' : 'text-gray-400'}`}>
                        {c.balance > 0 ? '+' : ''}{c.balance} шт
                      </span>
                    ) : (
                      <span className="text-xs text-gray-300 whitespace-nowrap">новый</span>
                    )}
                  </div>
                  <div className="text-xs text-gray-500 truncate">
                    {c.is_pool_owner
                      ? <span className="text-indigo-700">Забор распределяется по блокам пропорционально балансу</span>
                      : (
                        <>
                          {c.role || '—'}
                          {c.contact_person && <span> • {c.contact_person}</span>}
                          {c.phone && <span> • {c.phone}</span>}
                        </>
                      )}
                  </div>
                  {c.last_address && !c.is_pool_owner && (
                    <div className="text-xs text-emerald-700 truncate mt-0.5" title={c.last_address}>📍 {c.last_address}</div>
                  )}
                </button>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}


// =====================================================================
// Main page
// =====================================================================
export default function AdminContainers() {
  const navigate = useNavigate();
  const [summary, setSummary] = useState(null);
  const [clients, setClients] = useState([]);
  const [movements, setMovements] = useState([]);
  const [daily, setDaily] = useState([]);
  const [invLog, setInvLog] = useState([]);
  const [aging, setAging] = useState(null);
  const [loading, setLoading] = useState(true);

  // Dialogs
  const [showAdjust, setShowAdjust] = useState(false);
  const [adjustForm, setAdjustForm] = useState({ user_id: '', direction: 'left', count: 1, note: '', is_pool_owner: false, network_id: null });
  const [showInventory, setShowInventory] = useState(false);
  const [invForm, setInvForm] = useState({ mode: 'set', total: 0, count: 1, reason: '' });

  const fetchAll = async () => {
    setLoading(true);
    const [s, c, m, d, l, a] = await Promise.allSettled([
      axios.get(`${CT}/summary`, { headers: authHeaders() }),
      axios.get(`${CT}/by-client`, { headers: authHeaders() }),
      axios.get(`${CT}/movements?days=14`, { headers: authHeaders() }),
      axios.get(`${CT}/daily-chart?days=30`, { headers: authHeaders() }),
      axios.get(`${CT}/inventory-log?limit=50`, { headers: authHeaders() }),
      axios.get(`${CT}/aging?threshold_days=15`, { headers: authHeaders() }),
    ]);
    if (s.status === 'fulfilled') setSummary(s.value.data);
    if (c.status === 'fulfilled') setClients(c.value.data);
    if (m.status === 'fulfilled') setMovements(m.value.data);
    if (d.status === 'fulfilled') setDaily(d.value.data);
    if (l.status === 'fulfilled') setInvLog(l.value.data);
    if (a.status === 'fulfilled') setAging(a.value.data);
    setLoading(false);
  };

  useEffect(() => { fetchAll(); }, []);

  const submitAdjust = async () => {
    try {
      await axios.post(`${CT}/manual-adjust`, adjustForm, { headers: authHeaders() });
      setShowAdjust(false);
      setAdjustForm({ user_id: '', direction: 'left', count: 1, note: '', is_pool_owner: false, network_id: null });
      fetchAll();
    } catch (e) {
      alert('Ошибка: ' + (e?.response?.data?.detail || e.message));
    }
  };

  const openInventoryDialog = (mode) => {
    setInvForm({
      mode,
      total: summary?.total_owned || 0,
      count: 1,
      reason: '',
    });
    setShowInventory(true);
  };

  const submitInventory = async () => {
    try {
      if (invForm.mode === 'set') {
        await axios.put(`${CT}/inventory`, {
          total: parseInt(invForm.total || 0),
          reason: invForm.reason,
        }, { headers: authHeaders() });
      } else {
        await axios.post(`${CT}/inventory-adjust`, {
          type: invForm.mode, // 'receipt' or 'writeoff'
          count: parseInt(invForm.count || 1),
          reason: invForm.reason,
        }, { headers: authHeaders() });
      }
      setShowInventory(false);
      fetchAll();
    } catch (e) {
      alert('Ошибка: ' + (e?.response?.data?.detail || e.message));
    }
  };

  const filteredClients = useMemo(() => clients.filter(c => c.balance !== 0), [clients]);

  const invModeMeta = {
    set: { title: 'Установить общее число контейнеров', icon: Settings, color: 'text-gray-700' },
    receipt: { title: 'Поступление контейнеров в цех', icon: Truck, color: 'text-emerald-600' },
    writeoff: { title: 'Списание (разбито / утеряно)', icon: Trash2, color: 'text-red-600' },
  };
  const invMeta = invModeMeta[invForm.mode] || invModeMeta.set;
  const InvIcon = invMeta.icon;

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => navigate('/ru/admin')} data-testid="back-btn">
              <ArrowLeft className="w-4 h-4 mr-1" /> Назад
            </Button>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <Package className="w-6 h-6 text-indigo-600" />
              Учёт контейнеров
            </h1>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" size="sm" onClick={fetchAll} data-testid="refresh-btn">
              <RefreshCw className="w-4 h-4 mr-1" /> Обновить
            </Button>
            <Button variant="outline" size="sm" onClick={() => openInventoryDialog('receipt')} data-testid="receipt-btn" className="text-emerald-700 border-emerald-200">
              <Truck className="w-4 h-4 mr-1" /> Поступление
            </Button>
            <Button variant="outline" size="sm" onClick={() => openInventoryDialog('writeoff')} data-testid="writeoff-btn" className="text-red-700 border-red-200">
              <Trash2 className="w-4 h-4 mr-1" /> Списание
            </Button>
            <Button variant="outline" size="sm" onClick={() => openInventoryDialog('set')} data-testid="set-total-btn">
              <Settings className="w-4 h-4 mr-1" /> Общий запас
            </Button>
            <Button size="sm" onClick={() => setShowAdjust(true)} data-testid="manual-adjust-btn">
              <Plus className="w-4 h-4 mr-1" /> Движение по клиенту
            </Button>
          </div>
        </div>

        {/* KPIs */}
        {summary && (
          <>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
              <Card data-testid="kpi-total">
                <CardContent className="p-4">
                  <p className="text-xs text-gray-500 mb-1 flex items-center gap-1"><Package className="w-3 h-3"/>Всего контейнеров</p>
                  <p className="text-3xl font-bold text-indigo-700">{fmt(summary.total_owned)}</p>
                  <p className="text-xs text-gray-400 mt-1">в собственности Айнанан</p>
                </CardContent>
              </Card>
              <Card data-testid="kpi-at-workshop">
                <CardContent className="p-4">
                  <p className="text-xs text-gray-500 mb-1 flex items-center gap-1"><Archive className="w-3 h-3"/>В цеху сейчас</p>
                  <p className={`text-3xl font-bold ${summary.at_workshop < 0 ? 'text-red-600' : 'text-emerald-600'}`}>{fmt(summary.at_workshop)}</p>
                  <p className="text-xs text-gray-400 mt-1">{summary.total_owned > 0 ? `${Math.round(Math.max(0, summary.at_workshop) / summary.total_owned * 100)}% от общего` : 'задайте общий запас'}</p>
                </CardContent>
              </Card>
              <Card data-testid="kpi-at-clients">
                <CardContent className="p-4">
                  <p className="text-xs text-gray-500 mb-1">У клиентов сейчас</p>
                  <p className="text-3xl font-bold text-amber-600">{fmt(summary.at_clients)}</p>
                  <p className="text-xs text-gray-400 mt-1">шт. на руках</p>
                </CardContent>
              </Card>
              {summary.discrepancy > 0 ? (
                <Card data-testid="kpi-discrepancy" className="border-red-200 bg-red-50">
                  <CardContent className="p-4">
                    <p className="text-xs text-red-700 mb-1 font-semibold">⚠ Расхождение</p>
                    <p className="text-3xl font-bold text-red-700">−{fmt(summary.discrepancy)}</p>
                    <p className="text-xs text-red-600 mt-1">у клиентов больше, чем есть в собственности</p>
                  </CardContent>
                </Card>
              ) : (
                <Card data-testid="kpi-movements-today">
                  <CardContent className="p-4">
                    <p className="text-xs text-gray-500 mb-1">Движений сегодня</p>
                    <p className="text-3xl font-bold text-gray-900">{fmt(summary.today.movements)}</p>
                    <p className="text-xs text-gray-400 mt-1">+{fmt(summary.today.left)} / −{fmt(summary.today.picked)}</p>
                  </CardContent>
                </Card>
              )}
            </div>
            {/* Visual breakdown bar */}
            {summary.total_owned > 0 && (
              <div className="mb-6">
                <div className="flex h-3 rounded-full overflow-hidden bg-gray-200" data-testid="inventory-bar">
                  <div className="bg-emerald-500" style={{ width: `${Math.max(0, summary.at_workshop) / summary.total_owned * 100}%` }} title={`В цеху: ${summary.at_workshop}`} />
                  <div className="bg-amber-500" style={{ width: `${Math.min(100, summary.at_clients / summary.total_owned * 100)}%` }} title={`У клиентов: ${summary.at_clients}`} />
                  {summary.discrepancy > 0 && (
                    <div className="bg-red-500" style={{ width: `${summary.discrepancy / summary.total_owned * 100}%` }} title={`Расхождение: ${summary.discrepancy}`} />
                  )}
                </div>
                <div className="flex justify-between text-xs text-gray-500 mt-1 px-1">
                  <span><span className="inline-block w-2 h-2 rounded-sm bg-emerald-500 mr-1"/>В цеху {summary.at_workshop}</span>
                  <span><span className="inline-block w-2 h-2 rounded-sm bg-amber-500 mr-1"/>У клиентов {summary.at_clients}</span>
                  <span className="text-gray-400">Всего {summary.total_owned}</span>
                </div>
              </div>
            )}
          </>
        )}

        <Tabs defaultValue="clients">
          <TabsList>
            <TabsTrigger value="clients">По клиентам</TabsTrigger>
            <TabsTrigger value="aging" data-testid="containers-tab-aging">
              ⏳ Зависшая тара{aging?.summary?.aging_clients ? ` (${aging.summary.aging_clients})` : ''}
            </TabsTrigger>
            <TabsTrigger value="movements">Журнал доставок</TabsTrigger>
            <TabsTrigger value="daily">Дневная динамика</TabsTrigger>
            <TabsTrigger value="invlog">Журнал запаса</TabsTrigger>
            <TabsTrigger value="config" data-testid="containers-tab-config">Настройка контейнеров</TabsTrigger>
          </TabsList>

          <TabsContent value="clients">
            <Card>
              <CardHeader>
                <CardTitle>Баланс контейнеров у клиентов ({filteredClients.length})</CardTitle>
                <p className="text-xs text-gray-500">Положительное число = клиенту оставлено больше, чем забрано назад</p>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm" data-testid="clients-table">
                    <thead className="bg-gray-50 border-b">
                      <tr>
                        <th className="text-left p-3 font-semibold">Клиент</th>
                        <th className="text-left p-3 font-semibold">Филиал</th>
                        <th className="text-left p-3 font-semibold">Контакт</th>
                        <th className="text-right p-3 font-semibold">Остаток</th>
                        <th className="text-right p-3 font-semibold text-emerald-600">Всего получено</th>
                        <th className="text-right p-3 font-semibold text-blue-600">Всего возвращено</th>
                        <th className="text-left p-3 font-semibold">Последняя доставка</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredClients.map(c => (
                        <tr
                          key={`${c.user_id || c.name}__${c.network_id || 'none'}`}
                          className="border-b hover:bg-gray-50"
                          data-testid={`client-balance-row-${c.user_id || 'na'}${c.network_id ? `-${c.network_id.slice(0,8)}` : ''}`}
                        >
                          <td className="p-3 font-medium">{c.name}</td>
                          <td className="p-3 text-blue-700">
                            {c.network_name ? (
                              <div>
                                <div className="font-medium">{c.network_name}</div>
                                {c.address && <div className="text-xs text-gray-400">📍 {c.address}</div>}
                              </div>
                            ) : (
                              <span className="text-gray-400">— Общее —</span>
                            )}
                          </td>
                          <td className="p-3 text-gray-600">
                            {c.contact_person || '—'}
                            {c.phone && <div className="text-xs text-gray-400">{c.phone}</div>}
                          </td>
                          <td className={`p-3 text-right font-bold ${c.balance > 0 ? 'text-amber-600' : c.balance < 0 ? 'text-red-600' : 'text-gray-400'}`}>
                            {c.balance > 0 ? `+${c.balance}` : c.balance}
                          </td>
                          <td className="p-3 text-right text-emerald-600">{c.total_left}</td>
                          <td className="p-3 text-right text-blue-600">{c.total_picked}</td>
                          <td className="p-3 text-gray-600">{formatTime(c.last_delivery)}</td>
                        </tr>
                      ))}
                      {filteredClients.length === 0 && !loading && (
                        <tr><td colSpan="7" className="p-6 text-center text-gray-400">Нет активных балансов</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="aging">
            {/* Aging summary */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
              <Card className="border-l-4 border-l-gray-300" data-testid="aging-total">
                <CardContent className="pt-4">
                  <div className="text-xs text-gray-500">Всего у клиентов</div>
                  <div className="text-2xl font-bold text-gray-700">{aging?.summary?.total_containers ?? '—'} шт</div>
                  <div className="text-xs text-gray-400">{aging?.summary?.total_clients ?? 0} клиентов</div>
                </CardContent>
              </Card>
              <Card className="border-l-4 border-l-red-500" data-testid="aging-risk">
                <CardContent className="pt-4">
                  <div className="text-xs text-gray-500 flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> Зависло (≥15 дн)</div>
                  <div className="text-2xl font-bold text-red-600">{aging?.summary?.aging_containers ?? '—'} шт</div>
                  <div className="text-xs text-gray-400">{aging?.summary?.aging_clients ?? 0} клиентов · риск потери</div>
                </CardContent>
              </Card>
              {aging?.buckets && ['d15_30', 'd30p'].map((k) => (
                <Card key={k} className="border-l-4 border-l-amber-300" data-testid={`aging-bucket-${k}`}>
                  <CardContent className="pt-4">
                    <div className="text-xs text-gray-500 flex items-center gap-1"><Clock className="w-3 h-3" /> {aging.buckets[k].label}</div>
                    <div className="text-2xl font-bold text-amber-600">{aging.buckets[k].containers} шт</div>
                    <div className="text-xs text-gray-400">{aging.buckets[k].clients} клиентов</div>
                  </CardContent>
                </Card>
              ))}
            </div>

            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <Clock className="w-5 h-5 text-red-600" /> Забытая тара — давно не было доставки ({aging?.aging_list?.length ?? 0})
                </CardTitle>
                <p className="text-xs text-gray-500">Клиент держит контейнеры, а доставки давно не было — стоит напомнить и забрать</p>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm" data-testid="aging-table">
                    <thead className="bg-gray-50 border-b">
                      <tr>
                        <th className="text-left p-3 font-semibold">Клиент / филиал</th>
                        <th className="text-left p-3 font-semibold">Контакт</th>
                        <th className="text-right p-3 font-semibold">Остаток</th>
                        <th className="text-right p-3 font-semibold">Возраст</th>
                        <th className="text-left p-3 font-semibold">Последняя доставка</th>
                        <th className="text-center p-3 font-semibold">Связаться</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(aging?.aging_list || []).map((c) => {
                        let wa = (c.phone || '').replace(/\D/g, '');
                        if (wa.length === 11 && wa.startsWith('8')) wa = '7' + wa.slice(1);
                        return (
                          <tr key={`${c.user_id || c.name}__${c.network_id || 'none'}`} className="border-b hover:bg-gray-50" data-testid={`aging-row-${c.user_id || 'na'}`}>
                            <td className="p-3">
                              <div className="font-medium">{c.name}</div>
                              {c.network_name && <div className="text-xs text-blue-600">{c.network_name}</div>}
                              {c.address && <div className="text-xs text-gray-400">📍 {c.address}</div>}
                            </td>
                            <td className="p-3 text-gray-600">
                              {c.contact_person || '—'}
                              {c.phone && <div className="text-xs text-gray-400">{c.phone}</div>}
                            </td>
                            <td className="p-3 text-right font-bold text-amber-600">+{c.balance}</td>
                            <td className={`p-3 text-right font-semibold ${c.age_days > 30 ? 'text-red-600' : 'text-amber-600'}`}>{c.age_days} дн</td>
                            <td className="p-3 text-gray-600">{formatTime(c.last_delivery)}</td>
                            <td className="p-3">
                              <div className="flex items-center justify-center gap-1">
                                {c.phone && (
                                  <a href={`tel:${c.phone}`} data-testid={`aging-call-${c.user_id}`}>
                                    <Button size="sm" variant="ghost" className="h-8 w-8 p-0" title="Позвонить"><Phone className="w-4 h-4 text-emerald-600" /></Button>
                                  </a>
                                )}
                                {wa && (
                                  <a href={`https://wa.me/${wa}`} target="_blank" rel="noreferrer" data-testid={`aging-wa-${c.user_id}`}>
                                    <Button size="sm" variant="ghost" className="h-8 w-8 p-0" title="WhatsApp"><MessageCircle className="w-4 h-4 text-green-600" /></Button>
                                  </a>
                                )}
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                      {(!aging?.aging_list || aging.aging_list.length === 0) && !loading && (
                        <tr><td colSpan="6" className="p-6 text-center text-gray-400">Зависшей тары нет 🎉</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="movements">
            <Card>
              <CardHeader>
                <CardTitle>Журнал движений за 14 дней</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm" data-testid="movements-table">
                    <thead className="bg-gray-50 border-b">
                      <tr>
                        <th className="text-left p-3 font-semibold">Время</th>
                        <th className="text-left p-3 font-semibold">Заказ</th>
                        <th className="text-left p-3 font-semibold">Клиент</th>
                        <th className="text-right p-3 font-semibold text-emerald-600">Оставлено</th>
                        <th className="text-right p-3 font-semibold text-blue-600">Забрано</th>
                        <th className="text-right p-3 font-semibold">Δ</th>
                        <th className="text-left p-3 font-semibold">Статус</th>
                      </tr>
                    </thead>
                    <tbody>
                      {movements.map(m => (
                        <tr key={m.order_id} className="border-b hover:bg-gray-50">
                          <td className="p-3 text-gray-600">{formatTime(m.completed_at)}</td>
                          <td className="p-3 font-mono text-xs">#{m.order_number}</td>
                          <td className="p-3">
                            {m.client_name}
                            {m.contact_person && m.contact_person !== m.client_name && <div className="text-xs text-gray-400">{m.contact_person}</div>}
                          </td>
                          <td className="p-3 text-right text-emerald-600 font-medium">{m.containers_left ?? '—'}</td>
                          <td className="p-3 text-right text-blue-600 font-medium">{m.containers_picked_up ?? '—'}</td>
                          <td className={`p-3 text-right font-bold ${m.net_change > 0 ? 'text-amber-600' : m.net_change < 0 ? 'text-emerald-600' : 'text-gray-400'}`}>
                            {m.net_change > 0 ? '+' : ''}{m.net_change}
                          </td>
                          <td className="p-3">
                            <span className={`inline-block px-2 py-0.5 rounded text-xs ${m.delivery_status === 'delivered' ? 'bg-emerald-100 text-emerald-700' : m.delivery_status === 'problem' ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-700'}`}>
                              {m.delivery_status || '—'}
                            </span>
                            {m.problem_note && <div className="text-xs text-red-500 mt-0.5">{m.problem_note}</div>}
                          </td>
                        </tr>
                      ))}
                      {movements.length === 0 && !loading && (
                        <tr><td colSpan="7" className="p-6 text-center text-gray-400">Нет движений за период</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="daily">
            <Card>
              <CardHeader>
                <CardTitle>Дневной поток за 30 дней</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm" data-testid="daily-table">
                    <thead className="bg-gray-50 border-b">
                      <tr>
                        <th className="text-left p-3 font-semibold">Дата</th>
                        <th className="text-right p-3 font-semibold text-emerald-600">Оставлено</th>
                        <th className="text-right p-3 font-semibold text-blue-600">Забрано</th>
                        <th className="text-right p-3 font-semibold">Δ (баланс)</th>
                        <th className="text-right p-3 font-semibold">Движений</th>
                      </tr>
                    </thead>
                    <tbody>
                      {daily.slice().reverse().map(d => (
                        <tr key={d.date} className="border-b hover:bg-gray-50">
                          <td className="p-3">{d.date}</td>
                          <td className="p-3 text-right text-emerald-600">+{d.left}</td>
                          <td className="p-3 text-right text-blue-600">−{d.picked}</td>
                          <td className={`p-3 text-right font-bold ${d.net > 0 ? 'text-amber-600' : d.net < 0 ? 'text-emerald-600' : 'text-gray-400'}`}>
                            {d.net > 0 ? '+' : ''}{d.net}
                          </td>
                          <td className="p-3 text-right text-gray-600">{d.movements}</td>
                        </tr>
                      ))}
                      {daily.length === 0 && !loading && (
                        <tr><td colSpan="5" className="p-6 text-center text-gray-400">Нет данных</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="invlog">
            <Card>
              <CardHeader>
                <CardTitle>Изменения общего запаса</CardTitle>
                <p className="text-xs text-gray-500">Закупка новых, списание разбитых, ручные установки</p>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm" data-testid="invlog-table">
                    <thead className="bg-gray-50 border-b">
                      <tr>
                        <th className="text-left p-3 font-semibold">Дата</th>
                        <th className="text-left p-3 font-semibold">Тип</th>
                        <th className="text-right p-3 font-semibold">Δ</th>
                        <th className="text-right p-3 font-semibold">Итого после</th>
                        <th className="text-left p-3 font-semibold">Кто</th>
                        <th className="text-left p-3 font-semibold">Причина</th>
                      </tr>
                    </thead>
                    <tbody>
                      {invLog.map(l => {
                        const tColor = l.type === 'receipt' ? 'bg-emerald-100 text-emerald-700' : l.type === 'writeoff' ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-700';
                        const tLabel = l.type === 'receipt' ? 'Поступление' : l.type === 'writeoff' ? 'Списание' : 'Установка';
                        return (
                          <tr key={l.id} className="border-b hover:bg-gray-50">
                            <td className="p-3 text-gray-600">{formatTime(l.created_at)}</td>
                            <td className="p-3"><span className={`inline-block px-2 py-0.5 rounded text-xs ${tColor}`}>{tLabel}</span></td>
                            <td className={`p-3 text-right font-bold ${l.delta > 0 ? 'text-emerald-600' : l.delta < 0 ? 'text-red-600' : 'text-gray-400'}`}>
                              {l.delta > 0 ? '+' : ''}{l.delta}
                            </td>
                            <td className="p-3 text-right font-semibold">{l.new_total}</td>
                            <td className="p-3 text-gray-600 text-xs">{l.changed_by || '—'}</td>
                            <td className="p-3 text-gray-600">{l.reason || '—'}</td>
                          </tr>
                        );
                      })}
                      {invLog.length === 0 && !loading && (
                        <tr><td colSpan="6" className="p-6 text-center text-gray-400">Изменений ещё не было</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="config" data-testid="containers-config-content">
            <ContainersConfigPanel />
          </TabsContent>
        </Tabs>

        {/* Manual adjust dialog */}
        <Dialog open={showAdjust} onOpenChange={setShowAdjust}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Движение контейнеров по клиенту</DialogTitle>
              <DialogDescription>
                Запишите, что клиент получил или вернул контейнеры. Это повлияет только на баланс выбранного клиента, общий запас Айнанан не меняется.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              <div>
                <label className="text-sm font-medium block mb-1">Клиент</label>
                <ClientPicker
                  value={adjustForm.user_id}
                  onChange={(uid, c) => setAdjustForm(p => ({ ...p, user_id: uid, is_pool_owner: !!(c && c.is_pool_owner), network_id: (c && c.network_id) || null }))}
                />
              </div>
              {adjustForm.is_pool_owner && adjustForm.direction === 'left' && (
                <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2">
                  ⚠️ Для общего пула «Оставили клиенту» не имеет смысла — выберите конкретный блок, чтобы записать выдачу. Забор (минус) распределится автоматически.
                </div>
              )}
              {adjustForm.is_pool_owner && adjustForm.direction === 'picked' && (
                <div className="text-xs text-indigo-700 bg-indigo-50 border border-indigo-200 rounded p-2">
                  🔄 Эти {adjustForm.count} шт будут списаны с блоков пропорционально их текущему балансу.
                </div>
              )}
              <div>
                <label className="text-sm font-medium block mb-1">Тип движения</label>
                <Select value={adjustForm.direction} onValueChange={(v) => setAdjustForm(p => ({ ...p, direction: v }))}>
                  <SelectTrigger data-testid="adjust-direction-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="left">Оставили клиенту (+ к его балансу)</SelectItem>
                    <SelectItem value="picked">Забрали у клиента (− с его баланса)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium block mb-1">Количество, шт</label>
                <Input type="number" min="1" max="10000" value={adjustForm.count}
                       onChange={(e) => setAdjustForm(p => ({ ...p, count: parseInt(e.target.value || '1') }))}
                       data-testid="adjust-count-input" />
              </div>
              <div>
                <label className="text-sm font-medium block mb-1">Комментарий <span className="text-gray-400">(необязательно)</span></label>
                <Input value={adjustForm.note}
                       onChange={(e) => setAdjustForm(p => ({ ...p, note: e.target.value }))}
                       placeholder="Клиент принёс сам, доп. поставка и т.д."
                       data-testid="adjust-note-input" />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowAdjust(false)}>Отмена</Button>
              <Button onClick={submitAdjust} disabled={!adjustForm.user_id || adjustForm.count < 1} data-testid="adjust-submit-btn">
                Сохранить
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Inventory dialog (set total / receipt / writeoff) */}
        <Dialog open={showInventory} onOpenChange={setShowInventory}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className={`flex items-center gap-2 ${invMeta.color}`}>
                <InvIcon className="w-5 h-5" />
                {invMeta.title}
              </DialogTitle>
              <DialogDescription>
                {invForm.mode === 'set' && 'Установите точное число контейнеров, которыми владеет Айнанан. Используйте для инвентаризации.'}
                {invForm.mode === 'receipt' && 'Закупили новые или вернули с производства. Увеличит общий запас.'}
                {invForm.mode === 'writeoff' && 'Разбили, списали или утеряли. Уменьшит общий запас.'}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              {invForm.mode === 'set' ? (
                <div>
                  <label className="text-sm font-medium block mb-1">Всего контейнеров, шт</label>
                  <Input type="number" min="0" max="1000000" value={invForm.total}
                         onChange={(e) => setInvForm(p => ({ ...p, total: parseInt(e.target.value || '0') }))}
                         data-testid="inv-total-input" />
                  <p className="text-xs text-gray-500 mt-1">Текущее значение: {summary?.total_owned ?? 0} шт</p>
                </div>
              ) : (
                <div>
                  <label className="text-sm font-medium block mb-1">Количество, шт</label>
                  <Input type="number" min="1" max="100000" value={invForm.count}
                         onChange={(e) => setInvForm(p => ({ ...p, count: parseInt(e.target.value || '1') }))}
                         data-testid="inv-count-input" />
                  <p className="text-xs text-gray-500 mt-1">
                    {invForm.mode === 'receipt' ? `После: ${(summary?.total_owned ?? 0) + (parseInt(invForm.count || 0))} шт` : `После: ${(summary?.total_owned ?? 0) - (parseInt(invForm.count || 0))} шт`}
                  </p>
                </div>
              )}
              <div>
                <label className="text-sm font-medium block mb-1">Причина <span className="text-gray-400">(необязательно)</span></label>
                <Input value={invForm.reason}
                       onChange={(e) => setInvForm(p => ({ ...p, reason: e.target.value }))}
                       placeholder={
                         invForm.mode === 'receipt' ? 'Закупка партии, возврат с цеха' :
                         invForm.mode === 'writeoff' ? 'Разбили при доставке, потеряли' :
                         'Инвентаризация на дату X'
                       }
                       data-testid="inv-reason-input" />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowInventory(false)}>Отмена</Button>
              <Button onClick={submitInventory} data-testid="inv-submit-btn">
                Сохранить
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
